import React, { useRef, useEffect } from "react";

export interface ClassSelectorProps {
  availableWindows: {
    start: string | Date;
    end: string | Date;
    className: string;
    duration: number;
    remainingRooms: number;
  }[];

  selectedWindow: {
    start: string | Date;
    end: string | Date;
    className: string;
    duration: number;
    remainingRooms: number;
  } | null;

  setSelectedWindow: (w: {
    start: string | Date;
    end: string | Date;
    className: string;
    duration: number;
    remainingRooms: number;
  }) => void;

  setValue: (
    field: string,
    value: any,
    options?: { shouldValidate?: boolean }
  ) => void;

  normalizeDate: (d: string | Date) => Date;

  jumpToDate: (d: Date) => void;

  pluralizeRooms: (n: number) => string;

  isFixedPage: boolean;

  isDesktop: boolean;
}

export default function ClassSelector({
  availableWindows,
  selectedWindow,
  setSelectedWindow,
  setValue,
  normalizeDate,
  jumpToDate,
  pluralizeRooms,
  isFixedPage = false,
  isDesktop = false,
}: ClassSelectorProps) {

  const scrollRef = useRef<HTMLDivElement>(null);

  const scrollLeft = () => scrollRef.current?.scrollBy({ left: -200, behavior: "smooth" });

  const scrollRight = () => scrollRef.current?.scrollBy({ left: 200, behavior: "smooth" });

  // Sort windows chronologically
  const sortedWindows = [...availableWindows].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

  // Scroll selected tile into view when selection changes
  useEffect(() => {
    if (!scrollRef.current || !selectedWindow) return;

    const tiles = scrollRef.current.querySelectorAll<HTMLButtonElement>("button[data-window]");

    const match = Array.from(tiles).find(
      (btn) => btn.dataset.window === `${normalizeDate(selectedWindow.start).getTime()}-${normalizeDate(selectedWindow.end).getTime()}`,
    );

    match?.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [selectedWindow, normalizeDate]);

  // -------------------------------
  // 📱 MOBILE vs 🖥 DESKTOP TUNING
  // -------------------------------
  // const tileTextSize = isDesktop ? "text-sm" : "text-sm";
  // Fine-grained padding control (px-level)
  const tilePadding = isDesktop ? "px-[6px] py-[1px]" : "px-[4px] py-[8px]";
  const tileMinWidth = isDesktop ? "min-w-[130px]" : "min-w-[100px]";
  const classNameSize = isDesktop ? "text-sm font-semibold" : "text-[12px] font-semibold";
  const dateSize = isDesktop ? "text-sm" : "text-[12px]";
  const roomsSize = isDesktop ? "text-sm" : "text-[12px]";
  const tileLineHeight = isDesktop ? "leading-[0.9]" : "leading-[0.9]";

  return (
    <section className="w-full max-w-[600px] mx-auto border border-gray-300 rounded-lg p-1 bg-[#f7f0ea]">
      {sortedWindows.length === 0 ? (
        <div className="text-center text-[12px] font-semibold text-gray-500 py-1">
          {isFixedPage ? (
            <>
              Choose another class or pick a custom date{" "}
              <a href="/classes" className="text-blue-600 underline hover:text-blue-800">
                here
              </a>
              .
            </>
          ) : (
            <>
              There are no upcoming sessions scheduled right now. <br />
              You can still register by selecting your desired dates.
            </>
          )}
        </div>
      ) : (
        <div className="relative w-full">
          {/* Left Arrow */}
          <button
            type="button"
            onClick={scrollLeft}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-6 h-10 bg-white border border-gray-300 rounded-md shadow hover:bg-gray-100 flex items-center justify-center"
            aria-label="Scroll left"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 fill-gray-800" viewBox="0 0 24 24">
              <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
            </svg>
          </button>

          {/* Scrollable container */}
          <div
            ref={scrollRef}
            className="flex gap-1 justify-start overflow-x-auto snap-x [scroll-padding-left:1rem] snap-mandatory hide-scrollbar px-10 max-w-[510px] mx-auto"
          >
            {sortedWindows.map((w, i) => {
              const start = new Date(w.start);
              const end = new Date(w.end);
              const sameMonth = start.getMonth() === end.getMonth();

              const label = sameMonth
                ? `${start.toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })}–${end.toLocaleDateString("en-US", {
                    day: "numeric",
                  })}, ${end.getFullYear()}`
                : `${start.toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })}–${end.toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })}, ${end.getFullYear()}`;

              const isSelected =
                selectedWindow &&
                normalizeDate(selectedWindow.start).getTime() === normalizeDate(w.start).getTime() &&
                normalizeDate(selectedWindow.end).getTime() === normalizeDate(w.end).getTime();

              return (
                <button
                  key={i}
                  type="button"
                  data-window={`${normalizeDate(w.start).getTime()}-${normalizeDate(w.end).getTime()}`}
                  className={`
                    flex-shrink-0 rounded-md border snap-start
                    ${tilePadding}
                    ${tileMinWidth}
                    ${tileLineHeight}
                    ${isSelected ? "bg-blue-100 border-blue-400" : "bg-white border-gray-200"}
                  `}
                  onClick={() => {
                    setSelectedWindow(w);
                    setValue("ExperienceTitle", w.className, {
                      shouldValidate: true,
                    });
                    setValue("Experience", w.duration, {
                      shouldValidate: true,
                    });
                    jumpToDate(start);
                  }}
                >
                  <span className={classNameSize}>{w.className}</span>
                  <br />
                  <span className={dateSize}>{label}</span>
                  <br />
                  <span className={roomsSize}>🛌 {pluralizeRooms(w.remainingRooms)}</span>
                </button>
              );
            })}
          </div>

          {/* Right Arrow */}
          <button
            type="button"
            onClick={scrollRight}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-6 h-10 bg-white border border-gray-300 rounded-md shadow hover:bg-gray-100 flex items-center justify-center"
            aria-label="Scroll right"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 fill-gray-800" viewBox="0 0 24 24">
              <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z" />
            </svg>
          </button>
        </div>
      )}
    </section>
  );
}
